import React, { useState } from "react";
import { Box, Typography, Paper, Grow, Popper, MenuList, MenuItem } from "@mui/material";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { useNavigate } from "react-router";

const categories = [
    {
        name: "Clothing",
        subCategories: [
            {
                name: "Men",
                subCategories: [
                    { name: "Shirts" },
                    { name: "T-Shirts" },
                    {
                        name: "Jeans",
                        subCategories: [
                            { name: "Slim Fit" },
                            { name: "Regular Fit" },
                            { name: "Skinny" },
                        ],
                    },
                    { name: "Jackets & Coats" },
                    { name: "Shoes" },
                ],
            },
            {
                name: "Women",
                subCategories: [
                    { name: "Dresses" },
                    { name: "Tops & Blouses" },
                    {
                        name: "Skirts",
                        subCategories: [
                            { name: "Mini Skirt" },
                            { name: "Midi Skirt" },
                            { name: "Maxi Skirt" },
                        ],
                    },
                    { name: "Pants & Leggings" },
                    { name: "Shoes" },
                    { name: "Handbags" },
                ],
            },
            {
                name: "Kids",
                subCategories: [
                    { name: "Tops & Tees" },
                    { name: "Bottoms" },
                    { name: "Outerwear" },
                    { name: "Shoes" },
                    { name: "Accessories" },
                ],
            },
        ],
    },
    {
        name: "Lifestyle",
        subCategories: [
            {
                name: "Fitness & Sports",
                subCategories: [
                    { name: "Workout Gear" },
                    { name: "Sportswear" },
                    { name: "Yoga & Pilates" },
                    { name: "Accessories" },
                ],
            },
            {
                name: "Travel & Outdoors",
                subCategories: [
                    { name: "Bags" },
                    { name: "Luggage" },
                    { name: "Camping Gear" },
                    { name: "Travel Accessories" },
                ],
            },
            {
                name: "Health & Personal Care",
                subCategories: [
                    { name: "Vitamins & Supplements" },
                    { name: "Skincare" },
                    { name: "Haircare" },
                    { name: "Oral Care" },
                ],
            },
            {
                name: "Home & Living",
                subCategories: [
                    { name: "Decor & Accessories" },
                    { name: "Kitchen Essentials" },
                    { name: "Stationery" },
                    { name: "Pet Supplies" },
                ],
            },
        ],
    },
    {
        name: "Home & Living",
        subCategories: [
            { name: "Furniture" },
            { name: "Home Decor" },
            {
                name: "Kitchen & Dining",
                subCategories: [
                    { name: "Cookware" },
                    { name: "Tableware" },
                    { name: "Storage" },
                ],
            },
            { name: "Bedding & Bath" },
            { name: "Lighting" },
        ],
    },
    {
        name: "Other",
        subCategories: []
    },
];


// Recursive nested menu using Popper
const NestedMenuItem = ({ item }: any) => {
    const [openSub, setOpenSub] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const navigate = useNavigate();

    const handleSelectedCategory = (selectedItem: string) => navigate(`/browse?category=${selectedItem}`)

    return (
        <>
            <MenuItem
                onMouseEnter={(e) => { setAnchorEl(e.currentTarget); setOpenSub(true); }}
                onMouseLeave={() => setOpenSub(false)}
                sx={{ display: "flex", justifyContent: "space-between" }}
                onClick={() => handleSelectedCategory(item.name)}
            >
                {item.name}
                {item.subCategories && <ArrowRightIcon fontSize="small" />}
            </MenuItem>

            {item.subCategories && (
                <Popper
                    open={openSub}
                    anchorEl={anchorEl}
                    placement="right-start"
                    transition
                    disablePortal={false}
                    style={{ zIndex: 1200 }}
                >
                    {({ TransitionProps }) => (
                        <Grow {...TransitionProps}>
                            <Paper
                                onMouseEnter={() => setOpenSub(true)}
                                onMouseLeave={() => setOpenSub(false)}
                            >
                                <MenuList>
                                    {item.subCategories.map((sub: any) => (
                                        <NestedMenuItem key={sub.name} item={sub} />
                                    ))}
                                </MenuList>
                            </Paper>
                        </Grow>
                    )}
                </Popper>
            )}
        </>
    );
};

const CategoriesRow: React.FC = () => {
    const [activeCategory, setActiveCategory] = useState<string | null>(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const navigate = useNavigate();
    const handleSelectedCategory = (selectedItem: string) => navigate(`/browse?category=${selectedItem}`);

    return (
        <Box sx={{ display: "flex", gap: 4 }}>
            {categories.map((cat) => (
                <Box
                    key={cat.name}
                    onMouseEnter={(e) => { setActiveCategory(cat.name); setAnchorEl(e.currentTarget); }}
                    onMouseLeave={() => setActiveCategory(null)}
                >
                    <Typography
                        sx={{ cursor: "pointer", color: "#000", fontWeight: 500, "&:hover": { color: "#6C63FF" } }}
                        onClick={() => handleSelectedCategory(cat.name)}
                    >
                        {cat.name}
                    </Typography>

                    {/* Dropdown using Popper */}
                    {activeCategory === cat.name && anchorEl && (
                        <Popper
                            open={true}
                            anchorEl={anchorEl}
                            placement="bottom-start"
                            transition
                            disablePortal={false}
                            style={{ zIndex: 1200 }}
                        >
                            {({ TransitionProps }) => (
                                <Grow {...TransitionProps}>
                                    <Paper
                                        onMouseEnter={() => setActiveCategory(cat.name)}
                                        onMouseLeave={() => setActiveCategory(null)}
                                    >
                                        <MenuList>
                                            {cat.subCategories.map((sub: any) => (
                                                <NestedMenuItem key={sub.name} item={sub} />
                                            ))}
                                        </MenuList>
                                    </Paper>
                                </Grow>
                            )}
                        </Popper>
                    )}
                </Box>
            ))}
        </Box>
    );
};

export default CategoriesRow;
